import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SaveButton from "../../../../../components/SaveButton";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import { toast } from "../../../../../components/toastNotification";
import {
  addStateDept,
  savingDepartment,
  rowForEditDepartment,
  hasDepartmentName,
  editStateDepartment,
} from "../atoms/atoms";
import useAddOfficeDepartment from "../hooks/useAddOfficeDepartment";
import { IOfficeDepartmentDetails } from "../types";
import OfficeDepartmentDetails from "./OfficeDepartmentDetails";

interface DepartmentFormProps {
  isAdd: boolean;
  setIsAdd;
}

export const OfficeDepartmentForm: React.FC<DepartmentFormProps> = ({
  isAdd,
  setIsAdd,
}) => {
  const [isAddState, setAddState] = useRecoilState(addStateDept);
  const [saveModal, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(savingDepartment);
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditDepartment);
  const queryClient = useQueryClient();
  const [hasDepartment, setHasDepartment] = useRecoilState(hasDepartmentName);
  const setEditState = useSetRecoilState(editStateDepartment);
  const [newData, setNewData] = useState<IOfficeDepartmentDetails>();
  const useFormMethods = useForm<IOfficeDepartmentDetails>({
    defaultValues: { ...rowForEdit },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;

  // Add Office Department
  const {
    mutate: OfficeDepartmentRows,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddOfficeDepartment();

  const onSubmit = (data: IOfficeDepartmentDetails) => {
    setNewData({
      ...data,
      isActive: data.isActive ? true : false,
    });

    if (isAdd) {
      setSaveModal(true);
    } else {
      setIsSaving(false);
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };

  const handleSave = () => {
    setSaveModal(false);
    OfficeDepartmentRows(newData);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setHasDepartment(false);
    setIsSaving(false);
    setIsAdd(false);
  };

  // Add Result
  if (addSuccess && addResult) {
    if (addResult.status === 501 || addResult.data === "Duplicate") {
      toast.fire("Failed", "Office Department Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Office Department Added Successfully.", "success");
      queryClient.invalidateQueries();
      handleCancel();
      setAddState(false);
      setIsAdd(false);
      setHasDepartment(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "Office Department added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to Add Office Department! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries();
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Office Department! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries();
      setAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Office Department! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries();
    setAddState(false);
  }

  return (
    <>
      <div className=" p-3 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg">
              <div className="font-bold pt-1 pb text-primary text-base">
                Add New Office Department
              </div>
              <div className="flex justify-between gap-2 mr-2 pb-2 pt-3">
                <SaveButton
                  className={`flex items-left shadow-md lg:text-center font-semibold text-white justify-left gap-x-1 rounded-full bg-senary hover:bg-senary px-3 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary focus:ring-opacity-70
              ${!hasDepartment ? "opacity-50" : "opactiy-100"}`}
                  disabled={!hasDepartment}
                  onClick={() => setSaveModal(true)}
                  type="submit"
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 mr-3 border-secondary"></div>
          </form>
          <div>
            <OfficeDepartmentDetails />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={"Add Office Department"}
          qConfirmation={"Are you sure you want to Add this data?"}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default OfficeDepartmentForm;
